// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useCart } from '../contexts/CartContext';
import { ArrowLeft, ShoppingCart, Plus, Minus, CheckCircle, Leaf, MapPin } from 'lucide-react';

const fmt = (cents) => `$${(cents / 100).toFixed(2)}`;

function getProductEmoji(name = '') {
  const n = name.toLowerCase();
  if (n.includes('banana'))  return '🍌'; if (n.includes('apple'))  return '🍎';
  if (n.includes('spinach') || n.includes('lettuce')) return '🥬';
  if (n.includes('avocado')) return '🥑'; if (n.includes('tomato')) return '🍅';
  if (n.includes('milk'))    return '🥛'; if (n.includes('egg'))    return '🥚';
  if (n.includes('yogurt'))  return '🫙'; if (n.includes('cheese')) return '🧀';
  if (n.includes('chicken')) return '🍗';
  if (n.includes('beef') || n.includes('ground')) return '🥩';
  if (n.includes('salmon'))  return '🐟'; if (n.includes('bread') || n.includes('sourdough')) return '🍞';
  if (n.includes('croissant')) return '🥐'; if (n.includes('pasta')) return '🍝';
  if (n.includes('sauce'))   return '🫙'; if (n.includes('olive'))  return '🫒';
  if (n.includes('water'))   return '💧'; if (n.includes('juice'))  return '🍊';
  if (n.includes('coffee'))  return '☕';
  return '🛒';
}

export default function ProductDetailPage() {
  const { productId } = useParams();
  const { addItem, items } = useCart();
  const navigate      = useNavigate();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [qty, setQty]         = useState(1);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding]   = useState(false);
  const [added, setAdded]     = useState(false);

  const inCart = items.find(i => i.product_id === productId);

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  const fetchProduct = async () => {
    setLoading(true);
    const { data: prod } = await supabase
      .from('grocery_products')
      .select('*, grocery_categories(*)')
      .eq('id', productId)
      .single();

    setProduct(prod);

    if (prod?.category_id) {
      const { data: rel } = await supabase
        .from('grocery_products')
        .select('*')
        .eq('category_id', prod.category_id)
        .neq('id', productId)
        .eq('active', true)
        .limit(4);
      setRelated(rel || []);
    }
    setLoading(false);
  };

  const handleAddToCart = async () => {
    if (!product?.in_stock) return;
    setAdding(true);
    await addItem(product, qty);
    setAdding(false);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!product) return <div className="page-container" style={{ padding: '48px 0', textAlign: 'center', color: '#94A3B8' }}>Product not found.</div>;

  const isOnSale = product.sale_price_cents && product.sale_price_cents < product.price_cents;
  const price    = isOnSale ? product.sale_price_cents : product.price_cents;
  const savings  = isOnSale ? product.price_cents - product.sale_price_cents : 0;

  return (
    <div className="page-container" style={{ paddingTop: 24 }}>
      {/* Back */}
      <button className="btn btn-ghost btn-sm" style={{ marginBottom: 20 }} onClick={() => navigate(-1)}>
        <ArrowLeft size={16} /> Back
      </button>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 40, marginBottom: 48 }}>
        {/* Image */}
        <div style={{
          background: 'linear-gradient(135deg, #F0FDF4, #DCFCE7)',
          borderRadius: 20, display: 'flex', alignItems: 'center', justifyContent: 'center',
          minHeight: 320, fontSize: 120, border: '1px solid #BBF7D0', position: 'relative',
        }}>
          {product.is_organic && (
            <span style={{ position: 'absolute', top: 16, left: 16, background: '#16A34A', color: '#fff', fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 20 }}>ORGANIC</span>
          )}
          {isOnSale && (
            <span style={{ position: 'absolute', top: 16, right: 16, background: '#EF4444', color: '#fff', fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 20 }}>
              SAVE {fmt(savings)}
            </span>
          )}
          {getProductEmoji(product.name)}
        </div>

        {/* Info */}
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#16A34A', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
            {product.grocery_categories?.name || 'Grocery'}
          </div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: '#14532D', margin: '0 0 8px' }}>{product.name}</h1>

          {/* Badges */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            {product.is_organic && (
              <span className="badge badge-green" style={{ gap: 4 }}><Leaf size={12} /> Organic</span>
            )}
            {product.is_local && (
              <span className="badge badge-blue" style={{ gap: 4 }}><MapPin size={12} /> Local</span>
            )}
            {!product.in_stock && <span className="badge badge-red">Out of Stock</span>}
          </div>

          {product.description && (
            <p style={{ fontSize: 14, color: '#64748B', lineHeight: 1.6, marginBottom: 20 }}>{product.description}</p>
          )}

          {/* Price */}
          <div style={{ marginBottom: 24 }}>
            {isOnSale ? (
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                <span style={{ fontSize: 36, fontWeight: 900, color: '#EF4444' }}>{fmt(product.sale_price_cents)}</span>
                <span style={{ fontSize: 22, color: '#94A3B8', textDecoration: 'line-through' }}>{fmt(product.price_cents)}</span>
                <span style={{ fontSize: 14, color: '#EF4444', fontWeight: 700 }}>per {product.unit}</span>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ fontSize: 36, fontWeight: 900, color: '#14532D' }}>{fmt(product.price_cents)}</span>
                <span style={{ fontSize: 16, color: '#94A3B8' }}>per {product.unit}</span>
              </div>
            )}
          </div>

          {/* Qty + Add */}
          {product.in_stock && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#F0FDF4', borderRadius: 12, padding: '8px 16px' }}>
                  <button
                    onClick={() => setQty(q => Math.max(1, q - 1))}
                    style={{ width: 32, height: 32, borderRadius: 8, background: '#fff', border: '1px solid #BBF7D0', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <Minus size={16} color="#16A34A" />
                  </button>
                  <span style={{ fontSize: 18, fontWeight: 700, minWidth: 28, textAlign: 'center', color: '#14532D' }}>{qty}</span>
                  <button
                    onClick={() => setQty(q => q + 1)}
                    style={{ width: 32, height: 32, borderRadius: 8, background: '#16A34A', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <Plus size={16} color="#fff" />
                  </button>
                </div>
                <span style={{ fontSize: 16, fontWeight: 700, color: '#14532D' }}>= {fmt(price * qty)}</span>
              </div>

              <button
                className="btn btn-primary btn-lg"
                style={{ width: '100%', background: added ? '#059669' : undefined }}
                onClick={handleAddToCart}
                disabled={adding}
              >
                {added ? (
                  <><CheckCircle size={20} /> Added to Cart!</>
                ) : adding ? (
                  'Adding...'
                ) : inCart ? (
                  <><Plus size={20} /> Add {qty} More</>
                ) : (
                  <><ShoppingCart size={20} /> Add to Cart</>
                )}
              </button>

              {inCart && (
                <div style={{ fontSize: 13, color: '#64748B', textAlign: 'center', marginTop: 8 }}>
                  {inCart.quantity} already in your cart
                </div>
              )}
            </>
          )}

          {!product.in_stock && (
            <div style={{ background: '#FEE2E2', color: '#DC2626', padding: '12px 16px', borderRadius: 12, fontSize: 14, fontWeight: 600 }}>
              This product is currently out of stock.
            </div>
          )}
        </div>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <div style={{ borderTop: '1px solid #BBF7D0', paddingTop: 32 }}>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: '#14532D', marginBottom: 20 }}>More in {product.grocery_categories?.name}</h2>
          <div className="product-grid">
            {related.map(rel => (
              <div
                key={rel.id}
                className="product-card"
                onClick={() => navigate(`/products/${rel.id}`)}
                style={{ cursor: 'pointer' }}
              >
                <div className="product-img">
                  <span style={{ fontSize: 56 }}>{getProductEmoji(rel.name)}</span>
                </div>
                <div className="product-body">
                  <div className="product-name">{rel.name}</div>
                  <div className="product-unit">{rel.unit}</div>
                  <div className="product-price">{fmt(rel.sale_price_cents || rel.price_cents)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
